import {usePasteFileSelect} from '@github-ui/use-unified-file-select'
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ClipboardEventHandler,
  type DragEventHandler,
  type RefObject,
  type DragEvent,
} from 'react'

import {useMarkdownImageUpload, ACCEPTED_IMAGE_FILE_TYPES} from './use-markdown-image-upload'

export type UseMarkdownImageHandlingOptions = {
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  repositoryId?: string
}

export type DropTargetProps = {
  onDragEnter: DragEventHandler
  onDragOver: DragEventHandler
  onDragLeave: DragEventHandler
  onDrop: DragEventHandler
}

export type UseMarkdownImageHandlingReturn = {
  /** Pass as the `onPaste` prop on ChatInput. */
  onPaste: ClipboardEventHandler<HTMLTextAreaElement>
  /** Spread onto the drop target element for inline drag-and-drop support. */
  dropTargetProps: DropTargetProps
  /** True when a file is being dragged over the drop target. Use for visual feedback. */
  isDraggedOver: boolean
  isUploading: boolean
}

/**
 * A hook that provides `onPaste` and drag and drop handlers for a text area, implementing the
 * paste/drop → upload → markdown-inline lifecycle for images.
 *
 * Images are uploaded to dotcom's asset storage and inserted as standard markdown
 * `![filename](url)` syntax directly into the textarea text.
 *
 * The core upload logic is shared with `ImageAttachButton` via `useMarkdownImageUpload`.
 *
 * Usage:
 * ```tsx
 * const {onPaste, dropTargetProps, isUploading} = useMarkdownImageHandling({textAreaRef, repositoryId})
 * <textarea onPaste={onPaste} {...dropTargetProps} ref={textAreaRef} ... />
 * ```
 */
export function useMarkdownImageHandling({
  textAreaRef,
  repositoryId,
}: UseMarkdownImageHandlingOptions): UseMarkdownImageHandlingReturn {
  const {processFiles, isUploading} = useMarkdownImageUpload({
    textAreaRef,
    repositoryId,
  })

  const pasteTargetProps = usePasteFileSelect({
    onSelect: (accepted: File[]) => processFiles(accepted),
    multi: true,
    acceptedFileTypes: ACCEPTED_IMAGE_FILE_TYPES,
  })

  const onPaste: ClipboardEventHandler<HTMLTextAreaElement> = useCallback(
    event => {
      pasteTargetProps.onPaste(event)
    },
    [pasteTargetProps],
  )

  // Drag-and-drop handlers with a counter to correctly handle nested enter/leave events.
  // useDropFileSelect doesn't use a counter so isDraggedOver flickers when the cursor
  // moves across child elements of the drop target.
  const [isDraggedOver, setIsDraggedOver] = useState(false)
  const dragCounterRef = useRef(0)

  const dropTargetProps: DropTargetProps = useMemo(
    () => ({
      onDragEnter: (event: DragEvent) => {
        if (!event.dataTransfer.types.includes('Files')) return
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current++
        if (dragCounterRef.current === 1) setIsDraggedOver(true)
      },
      onDragOver: (event: DragEvent) => {
        if (!event.dataTransfer.types.includes('Files')) return
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = 'copy'
      },
      onDragLeave: (event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current--
        if (dragCounterRef.current <= 0) {
          dragCounterRef.current = 0
          setIsDraggedOver(false)
        }
      },
      onDrop: (event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current = 0
        setIsDraggedOver(false)
        if (event.dataTransfer.files.length > 0) {
          processFiles(event.dataTransfer.files)
        }
      },
    }),
    [processFiles],
  )

  return {
    onPaste,
    dropTargetProps,
    isDraggedOver,
    isUploading,
  }
}
